import { Flex, Link, Text, FormLayout } from "@wayflyer/flyui";
import { useIntl } from "react-intl";
import { useCustomerAppConfig } from "undercarriage";

import {
  BalanceSheetDocumentUploadPanel,
  ProfitAndLossDocumentUploadPanel,
} from "../../document-uploads-manager/components";
import { ReportingCurrencySelect } from "../../document-uploads-manager/components/reporting-currency-input";
import { useValidatedDocumentQuery } from "../../../queries/document-uploads/validated-documents-queries";
import { useFundingApplication } from "../../../queries/funding-application/queries";
import { UploadDocumentType } from "../../../types";

import { MESSAGES } from "./message-definitions";

export const AccountingUploadPanel = ({
  validationAttempted,
  selectedCurrency,
  onCurrencyChange,
}: {
  validationAttempted?: boolean;
  selectedCurrency?: string;
  onCurrencyChange?: (currency: string) => void;
}) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: fundingApplication } = useFundingApplication(customerDomain);
  const { data: validatedDocuments } = useValidatedDocumentQuery(customerDomain, fundingApplication.id);

  const plFiles = validatedDocuments?.find((d) => d.documentType === UploadDocumentType.PROFIT_AND_LOSS)?.data ?? [];
  const bsFiles = validatedDocuments?.find((d) => d.documentType === UploadDocumentType.BALANCE_SHEET)?.data ?? [];

  const hasAnyDocumentsUploaded = plFiles.length > 0 || bsFiles.length > 0;
  const currencyError =
    (hasAnyDocumentsUploaded || validationAttempted) && !selectedCurrency && !fundingApplication.accountingOptedOut
      ? intl.formatMessage(MESSAGES.missingAccountingCurrencyError)
      : undefined;
  const plError =
    validationAttempted && plFiles.length === 0 ? intl.formatMessage(MESSAGES.missingProfitAndLossError) : undefined;
  const bsError =
    validationAttempted && bsFiles.length === 0 ? intl.formatMessage(MESSAGES.missingBalanceSheetError) : undefined;

  return (
    <FormLayout>
      <Text>{intl.formatMessage(MESSAGES.accountingUploadPanelDescription)}</Text>
      <ReportingCurrencySelect
        initialValue={selectedCurrency}
        disabled={fundingApplication.accountingOptedOut}
        error={currencyError}
        onChange={(item) => onCurrencyChange?.(item.text)}
        helperText={intl.formatMessage(MESSAGES.reportingCurrencyHelperSlideoutText)}
      />
      <Flex direction="column" gap="2">
        <Text fontWeight="semiBold">{intl.formatMessage(MESSAGES.documentUploadProfitAndLossTitle)}</Text>
        <Text size="sm" color="placeholder">
          {intl.formatMessage(MESSAGES.plExampleLink, {
            a: (chunks: string) => (
              <Link url="/static/example-docs/profit-and-loss-12m-YTD.pdf" external>
                {chunks}
              </Link>
            ),
          })}
        </Text>
        <ProfitAndLossDocumentUploadPanel error={plError} />
      </Flex>
      <Flex direction="column" gap="2">
        <Text fontWeight="semiBold">{intl.formatMessage(MESSAGES.documentUploadBalanceSheetTitle)}</Text>
        <Text size="sm" color="placeholder">
          {intl.formatMessage(MESSAGES.bsExampleLink, {
            a: (chunks: string) => (
              <Link url="/static/example-docs/balance-sheet-12m-YTD.pdf" external>
                {chunks}
              </Link>
            ),
          })}
        </Text>
        <BalanceSheetDocumentUploadPanel error={bsError} />
      </Flex>
    </FormLayout>
  );
};
